import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Footprints, Save, Plus, Trash2, Trophy, ListOrdered, ShieldAlert, Music2, Settings2 } from "lucide-react";
import { raceApi, apiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const TA = (props) => (<textarea {...props} className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-[#0A1C30] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 ${props.className || ""}`} />);
const fmtTime = (s) => { const m = Math.floor(s / 60), sec = s % 60; return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`; };

export default function RaceAdmin() {
  const [tab, setTab] = useState("plan");
  const [cfg, setCfg] = useState(null);
  const [mes, setMes] = useState(1);
  const [grupo, setGrupo] = useState("");
  const [ranking, setRanking] = useState([]);
  const [runs, setRuns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState(null);
  const [year, setYear] = useState(0);

  useEffect(() => { raceApi.adminConfig().then(setCfg).catch((e) => toast.error(apiError(e))); raceApi.adminGroups().then(setGroups).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "rankings") raceApi.adminRanking(mes, grupo).then((d) => setRanking(d.ranking)).catch(() => {});
    if (tab === "registros") raceApi.adminRuns({ mes: mes || 0, grupo }).then(setRuns).catch(() => {});
    if (tab === "acumulado") raceApi.adminRankingTotal(year, grupo).then(setTotals).catch(() => {});
  }, [tab, mes, grupo, year]);

  const save = async () => { try { const d = await raceApi.saveConfig(cfg); setCfg(d); toast.success("Configuración guardada"); } catch (e) { toast.error(apiError(e)); } };
  const setPlan = (key, i, field, val) => { const p = [...cfg[key]]; p[i] = { ...p[i], [field]: field === "km" ? Number(val) : Number(val) }; setCfg({ ...cfg, [key]: p }); };

  if (!cfg) return <p className="text-sm text-[#6B7688]">Cargando…</p>;

  return (
    <div data-testid="race-admin" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#E9A400] text-white flex items-center justify-center"><Footprints className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A1C30]">Carrera Mensual</h1>
          <p className="text-sm text-[#6B7688]">Planeación de distancias, seguridad, música y rankings.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-black/5">
        {[["plan", "Plan de carreras", Settings2], ["seguridad", "Seguridad y música", ShieldAlert], ["rankings", "Rankings", Trophy], ["acumulado", "Acumulado", ListOrdered], ["registros", "Registros", ListOrdered]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`race-admin-tab-${id}`} onClick={() => setTab(id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-t-xl transition-colors ${tab === id ? "bg-[#0A1C30] text-white" : "text-[#6B7688] hover:bg-black/5"}`}><Icon className="w-4 h-4" /> {label}</button>
        ))}
      </div>

      {/* PLAN */}
      {tab === "plan" && (
        <div className="space-y-4 max-w-3xl">
          <div className="rounded-2xl border border-black/10 bg-white p-5 space-y-3">
            <div><Label className="text-xs">Título</Label><Input value={cfg.titulo} onChange={(e) => setCfg({ ...cfg, titulo: e.target.value })} /></div>
            <div><Label className="text-xs">Introducción</Label><TA rows={2} value={cfg.intro} onChange={(e) => setCfg({ ...cfg, intro: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Programas de CARRERA (separa con coma)</Label><Input value={(cfg.run_programs || []).join(", ")} onChange={(e) => setCfg({ ...cfg, run_programs: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
              <div><Label className="text-xs">Programas de CAMINATA (separa con coma)</Label><Input value={(cfg.walk_programs || []).join(", ")} onChange={(e) => setCfg({ ...cfg, walk_programs: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label className="text-xs">Peso def. carrera (kg)</Label><Input type="number" value={cfg.default_weight_run} onChange={(e) => setCfg({ ...cfg, default_weight_run: Number(e.target.value) })} /></div>
              <div><Label className="text-xs">Peso def. caminata (kg)</Label><Input type="number" value={cfg.default_weight_walk} onChange={(e) => setCfg({ ...cfg, default_weight_walk: Number(e.target.value) })} /></div>
              <div><Label className="text-xs">Coef. kcal carrera</Label><Input type="number" step="0.001" value={cfg.cal_coeff_run} onChange={(e) => setCfg({ ...cfg, cal_coeff_run: Number(e.target.value) })} /></div>
              <div><Label className="text-xs">Coef. kcal caminata</Label><Input type="number" step="0.001" value={cfg.cal_coeff_walk} onChange={(e) => setCfg({ ...cfg, cal_coeff_walk: Number(e.target.value) })} /></div>
            </div>
          </div>

          {[["plan_run", "Distancias de CARRERA (km por mes)"], ["plan_walk", "Distancias de CAMINATA (km por mes)"]].map(([key, label]) => (
            <div key={key} className="rounded-2xl border border-black/10 bg-white p-5">
              <div className="text-sm font-extrabold text-[#0A1C30] mb-3">{label}</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {(cfg[key] || []).map((p, i) => (
                  <div key={i} className="text-center">
                    <div className="text-[11px] font-bold text-[#9AA3B2]">Mes {p.mes}</div>
                    <Input data-testid={`race-${key}-${p.mes}`} type="number" step="0.5" value={p.km} onChange={(e) => setPlan(key, i, "km", e.target.value)} className="text-center mt-1" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button data-testid="race-save-plan" onClick={save} className="flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl bg-[var(--gold)] text-[#0A1C30]"><Save className="w-4 h-4" /> Guardar plan</button>
        </div>
      )}

      {/* SEGURIDAD Y MÚSICA */}
      {tab === "seguridad" && (
        <div className="space-y-4 max-w-2xl">
          <div className="rounded-2xl border border-black/10 bg-white p-5">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2 mb-3"><ShieldAlert className="w-4 h-4 text-red-600" /> Medidas de seguridad</div>
            {(cfg.safety_warnings || []).map((s, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <TA rows={2} value={s} onChange={(e) => { const a = [...cfg.safety_warnings]; a[i] = e.target.value; setCfg({ ...cfg, safety_warnings: a }); }} />
                <button onClick={() => setCfg({ ...cfg, safety_warnings: cfg.safety_warnings.filter((_, x) => x !== i) })} className="p-2 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setCfg({ ...cfg, safety_warnings: [...(cfg.safety_warnings || []), ""] })} className="text-xs font-bold text-[#E9A400] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar medida</button>
          </div>
          <div className="rounded-2xl border border-black/10 bg-white p-5">
            <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2 mb-3"><Music2 className="w-4 h-4 text-[#1DB954]" /> Playlists de Spotify (sugeridas por Supera)</div>
            {(cfg.spotify_playlists || []).map((p, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Input placeholder="Nombre" value={p.label} onChange={(e) => { const a = [...cfg.spotify_playlists]; a[i] = { ...a[i], label: e.target.value }; setCfg({ ...cfg, spotify_playlists: a }); }} className="w-40" />
                <Input placeholder="URL de Spotify" value={p.url} onChange={(e) => { const a = [...cfg.spotify_playlists]; a[i] = { ...a[i], url: e.target.value }; setCfg({ ...cfg, spotify_playlists: a }); }} className="flex-1" />
                <button onClick={() => setCfg({ ...cfg, spotify_playlists: cfg.spotify_playlists.filter((_, x) => x !== i) })} className="p-2 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setCfg({ ...cfg, spotify_playlists: [...(cfg.spotify_playlists || []), { label: "", url: "", type: "playlist" }] })} className="text-xs font-bold text-[#1DB954] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar playlist</button>
          </div>
          <button data-testid="race-save-seguridad" onClick={save} className="flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl bg-[var(--gold)] text-[#0A1C30]"><Save className="w-4 h-4" /> Guardar</button>
        </div>
      )}

      {/* Filtros compartidos */}
      {(tab === "rankings" || tab === "registros") && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-[#6B7688]">Mes:</span>
          <select data-testid="race-admin-mes" value={mes} onChange={(e) => setMes(Number(e.target.value))} className="rounded-lg border border-black/10 px-3 py-1.5 text-sm font-bold">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>Mes {m}</option>)}
          </select>
          <span className="text-sm font-bold text-[#6B7688]">Grupo:</span>
          <select data-testid="race-admin-grupo" value={grupo} onChange={(e) => setGrupo(e.target.value)} className="rounded-lg border border-black/10 px-3 py-1.5 text-sm">
            <option value="">Todos</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      )}

      {/* ACUMULADO */}
      {tab === "acumulado" && (
        <div className="space-y-4" data-testid="race-admin-acumulado">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#6B7688]">Periodo:</span>
            <select data-testid="race-acum-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-lg border border-black/10 px-3 py-1.5 text-sm font-bold">
              <option value={0}>Histórico total</option>
              <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
              <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
            </select>
            <span className="text-sm font-bold text-[#6B7688]">Grupo:</span>
            <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className="rounded-lg border border-black/10 px-3 py-1.5 text-sm">
              <option value="">Todos</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-black/10 bg-white overflow-hidden">
              <div className="px-4 py-2 text-xs font-extrabold text-[#6B7688] bg-black/[0.02]">POR USUARIO (km acumulados)</div>
              {(!totals || totals.usuarios.length === 0) ? <p className="text-sm text-[#6B7688] p-5 text-center">Sin datos.</p> :
                totals.usuarios.map((u) => (
                  <div key={u.posicion} className="flex items-center gap-3 px-4 py-2.5 border-b border-black/5">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-extrabold ${u.posicion <= 3 ? "bg-[#E9A400] text-white" : "bg-black/5 text-[#6B7688]"}`}>{u.posicion}</span>
                    <span className="flex-1 text-sm font-bold text-[#0A1C30] truncate">{u.nombre} <span className="text-[10px] text-[#9AA3B2]">{u.grupo}</span></span>
                    <span className="text-sm font-extrabold text-[#E9A400] tabular-nums">{u.total_km} km</span>
                  </div>
                ))}
            </div>
            <div className="rounded-2xl border border-black/10 bg-white overflow-hidden">
              <div className="px-4 py-2 text-xs font-extrabold text-[#6B7688] bg-black/[0.02]">POR GRUPO (km acumulados)</div>
              {(!totals || totals.grupos.length === 0) ? <p className="text-sm text-[#6B7688] p-5 text-center">Sin datos.</p> :
                totals.grupos.map((gp, i) => (
                  <div key={gp.grupo} className="flex items-center gap-3 px-4 py-2.5 border-b border-black/5">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-extrabold ${i < 3 ? "bg-[#0A1C30] text-white" : "bg-black/5 text-[#6B7688]"}`}>{i + 1}</span>
                    <span className="flex-1 text-sm font-bold text-[#0A1C30] truncate">{gp.grupo} <span className="text-[10px] text-[#9AA3B2]">({gp.usuarios} usuarios)</span></span>
                    <span className="text-sm font-extrabold text-[#E9A400] tabular-nums">{gp.total_km} km</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* RANKINGS */}
      {tab === "rankings" && (
        <div className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="race-admin-rankings">
          {ranking.length === 0 ? <p className="text-sm text-[#6B7688] p-5 text-center">Sin participantes en este filtro.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-[#6B7688] border-b border-black/5">
                <th className="py-2 px-4">#</th><th className="py-2">Nombre</th><th className="py-2">Programa</th><th className="py-2">Grupo</th><th className="py-2 text-right">Distancia</th><th className="py-2 text-right px-4">Tiempo</th>
              </tr></thead>
              <tbody>
                {ranking.map((r) => (
                  <tr key={r.posicion} className="border-b border-black/5">
                    <td className="py-2 px-4 font-extrabold text-[#E9A400]">{r.posicion}</td>
                    <td className="py-2 font-bold text-[#0A1C30]">{r.nombre}{!r.completo && <span className="ml-1 text-[10px] text-[#b45309]">· incompleta</span>}</td>
                    <td className="py-2 text-[#6B7688]">{r.programa}</td>
                    <td className="py-2 text-[#6B7688]">{r.grupo || "—"}</td>
                    <td className="py-2 text-right tabular-nums">{r.distance_km.toFixed(2)} km</td>
                    <td className="py-2 text-right px-4 font-bold tabular-nums">{fmtTime(r.duration_sec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* REGISTROS */}
      {tab === "registros" && (
        <div className="space-y-2" data-testid="race-admin-registros">
          {runs.length === 0 ? <p className="text-sm text-[#6B7688]">Sin registros en este filtro.</p> : runs.map((r) => (
            <div key={r.id} className="rounded-xl border border-black/10 bg-white p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#E9A400]/15 flex items-center justify-center text-[#E9A400] font-extrabold text-sm">M{r.mes}</span>
              <div className="flex-1">
                <div className="text-sm font-bold text-[#0A1C30]">{r.nombre} · {r.distance_km.toFixed(2)} km {r.completo && <span className="text-emerald-600">✓</span>}</div>
                <div className="text-xs text-[#6B7688]">{fmtTime(r.duration_sec)} · {r.calories} kcal · {r.tipo} · {r.programa} {r.grupo && `· ${r.grupo}`}</div>
              </div>
              <div className="text-xs text-[#9AA3B2]">{new Date(r.created_at).toLocaleDateString("es-MX")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
